import { ChangeRequestChange, ChangeRequestStatus } from "./queries";
import { ChangeType, changeTypeToDisplayName } from "./settings/utils";
import { toFormat } from "@/lib/dateHelpers";

const changeTypeForRequestType = {
  BackofficeAddEmploymentChange: ChangeType.start_new_employment,
  BackofficeAddUnlistedEmploymentChange: ChangeType.start_new_unlisted_employment,
  BackofficeUpdateEmploymentChange: ChangeType.change_employment,
  BackofficeUpdateUnlistedEmploymentChange: ChangeType.change_employment_unlisted,
  BackofficeLifeMemberChange: ChangeType.life_member
} as const;

export function formatChangeRequestType(type: ChangeRequestChange["__typename"]): string {
  return changeTypeToDisplayName(changeTypeForRequestType[type]);
}

export const formatStage = (status: ChangeRequestStatus): string =>
  ({
    [ChangeRequestStatus.APPROVED]: "Completed",
    [ChangeRequestStatus.CANCELED]: "Canceled",
    [ChangeRequestStatus.PENDING]: "Needs Approval",
    [ChangeRequestStatus.REJECTED]: "Rejected"
  })[status];

const formatWorkingLocation = (value?: string | null): string | null => {
  if (!value) return null;
  if (value === "ON_SITE" || value === "on_site") return "On-site";
  if (value === "REMOTE" || value === "remote") return "Remote";
  return value;
};

export type EmploymentChangeField =
  | "title"
  | "description"
  | "facility"
  | "isPrimaryFacility"
  | "effectiveDate"
  | "classification"
  | "workingLocation";

type NonActiveClassificationOnlyChangeField = "activeYearsAtRequest" | "memberAgeAtRequest" | "requestedAt";

export type ChangeField = EmploymentChangeField | NonActiveClassificationOnlyChangeField;

const EMPLOYMENT_CHANGE_FIELD_LABELS: Record<EmploymentChangeField, string> = {
  title: "Title",
  description: "Description",
  facility: "Facility",
  isPrimaryFacility: "Is Primary Facility",
  effectiveDate: "Effective Date",
  classification: "Classification",
  workingLocation: "Working Location"
};

const NON_ACTIVE_CLASSIFICATION_ONLY_FIELD_LABELS: Record<NonActiveClassificationOnlyChangeField, string> = {
  activeYearsAtRequest: "Active Years at Time of Request",
  memberAgeAtRequest: "Member Age at Time of Request",
  requestedAt: "Requested Date"
};

export function changeFieldLabel(field: ChangeField): string {
  if (field in NON_ACTIVE_CLASSIFICATION_ONLY_FIELD_LABELS) {
    return NON_ACTIVE_CLASSIFICATION_ONLY_FIELD_LABELS[field as NonActiveClassificationOnlyChangeField];
  }

  return EMPLOYMENT_CHANGE_FIELD_LABELS[field as EmploymentChangeField];
}

const DAYS_PER_YEAR = 365;

function formatActiveYearsAtRequest(activeDaysAtRequest?: number | null): string | undefined {
  if (activeDaysAtRequest == null) return undefined;

  const years = Math.floor(activeDaysAtRequest / DAYS_PER_YEAR);
  return years.toString();
}

function formatClassificationDisplay(code?: string | null, label?: string | null): string | null {
  if (!code || !label) return null;

  return `${label} (${code})`;
}

export type FormattedChange = {
  id: ChangeField;
  requested?: string;
  existing: string | null;
};

type CurrentEmploymentLike = {
  title: string;
  formattedTitle?: string;
  description?: string | null;
  classification?: string | null;
  effectiveDate: string;
  workingLocation?: string | null;
  facility?: { name: string };
  isPrimaryFacility?: boolean;
};

function formatExistingEmploymentValues(currentEmployment: CurrentEmploymentLike) {
  const existingTitle = currentEmployment.formattedTitle || currentEmployment.title;

  return {
    title: existingTitle,
    description: currentEmployment.description ?? null,
    facility: currentEmployment.facility?.name ?? null,
    isPrimaryFacility:
      currentEmployment.isPrimaryFacility == null ? null : currentEmployment.isPrimaryFacility.toString(),
    effectiveDate: toFormat(currentEmployment.effectiveDate)!,
    classification: currentEmployment.classification ?? null,
    workingLocation: formatWorkingLocation(currentEmployment.workingLocation)
  };
}

export function formatChanges(change: ChangeRequestChange, status: ChangeRequestStatus): FormattedChange[] {
  let fields: FormattedChange[] = [];

  switch (change.__typename) {
    case "BackofficeAddEmploymentChange":
    case "BackofficeAddUnlistedEmploymentChange": {
      const existingEmployment =
        status === ChangeRequestStatus.PENDING && change.currentEmployment
          ? formatExistingEmploymentValues(change.currentEmployment)
          : null;

      fields = [
        { id: "title", requested: change.formattedTitle || change.title, existing: existingEmployment?.title ?? null },
        { id: "description", requested: change.description, existing: existingEmployment?.description ?? null },
        { id: "facility", requested: change.facility.name, existing: existingEmployment?.facility ?? null },
        {
          id: "isPrimaryFacility",
          requested: change.isPrimaryFacility.toString(),
          existing: existingEmployment?.isPrimaryFacility ?? null
        },
        {
          id: "effectiveDate",
          requested: toFormat(change.effectiveDate)!,
          existing: existingEmployment?.effectiveDate ?? null
        },
        {
          id: "classification",
          requested: change.classification ?? undefined,
          existing: existingEmployment?.classification ?? null
        },
        {
          id: "workingLocation",
          requested: formatWorkingLocation(change.workingLocation) ?? undefined,
          existing: existingEmployment?.workingLocation ?? null
        }
      ];
      break;
    }
    case "BackofficeUpdateEmploymentChange":
    case "BackofficeUpdateUnlistedEmploymentChange": {
      const requestedTitle = change.formattedTitle || change.title;
      const existingTitle = change.currentEmployment.formattedTitle || change.currentEmployment.title;
      fields = [
        { id: "title", requested: requestedTitle, existing: existingTitle },
        { id: "description", requested: change.description, existing: change.currentEmployment.description },
        {
          id: "effectiveDate",
          requested: toFormat(change.effectiveDate)!,
          existing: toFormat(change.currentEmployment.effectiveDate)!
        },
        {
          id: "classification",
          requested: change.classification ?? undefined,
          existing: change.currentEmployment.classification
        },
        {
          id: "workingLocation",
          requested: formatWorkingLocation(change.workingLocation) ?? undefined,
          existing: formatWorkingLocation(change.currentEmployment.workingLocation)
        }
      ];
      break;
    }
    case "BackofficeLifeMemberChange":
      fields = formatNonActiveClassificationChanges(change, status);
      break;
  }

  return fields;
}

function formatNonActiveClassificationChanges(
  change: Extract<ChangeRequestChange, { __typename: "BackofficeLifeMemberChange" }>,
  status: ChangeRequestStatus
): FormattedChange[] {
  const requestedClassification = formatClassificationDisplay(
    change.requestedClassification,
    change.requestedClassificationLabel
  );
  const existingClassification =
    status === ChangeRequestStatus.PENDING
      ? formatClassificationDisplay(change.currentClassification, change.currentClassificationLabel)
      : null;

  const fields: FormattedChange[] = [
    { id: "classification", requested: requestedClassification ?? undefined, existing: existingClassification },
    {
      id: "activeYearsAtRequest",
      requested: formatActiveYearsAtRequest(change.activeDaysAtRequest),
      existing: null
    },
    {
      id: "memberAgeAtRequest",
      requested: change.memberAgeAtRequest?.toString(),
      existing: null
    },
    {
      id: "requestedAt",
      requested: change.requestedAt ? (toFormat(change.requestedAt) ?? undefined) : undefined,
      existing: null
    }
  ];

  return fields;
}

export function isPrimaryEmploymentReplacement(change: ChangeRequestChange, status: ChangeRequestStatus): boolean {
  if (status !== ChangeRequestStatus.PENDING) return false;

  switch (change.__typename) {
    case "BackofficeAddEmploymentChange":
    case "BackofficeAddUnlistedEmploymentChange":
      return change.isPrimaryFacility && !!change.currentEmployment;
    default:
      return false;
  }
}

function hasVisibleValue({ requested, existing }: FormattedChange): boolean {
  return !!(requested || existing);
}

export function visibleChanges(change: ChangeRequestChange, status: ChangeRequestStatus): FormattedChange[] {
  const fields = formatChanges(change, status);

  if (isPrimaryEmploymentReplacement(change, status)) {
    return fields.filter(hasVisibleValue);
  }

  return fields.filter(({ requested, existing }) => requested !== existing);
}

export function getVisibleChangeCount(change: ChangeRequestChange, status: ChangeRequestStatus): number {
  return visibleChanges(change, status).length;
}

export function buildSectionChangeRequestListPath(
  sectionId: string,
  options: {
    expandChanges?: string;
    highlightRow?: string;
    stage?: ChangeRequestStatus;
  } = {}
): string {
  const params = new URLSearchParams();

  if (options.stage) {
    params.set("stage", options.stage);
  }
  if (options.expandChanges) {
    params.set("expandChanges", options.expandChanges);
  }
  if (options.highlightRow) {
    params.set("highlightRow", options.highlightRow);
  }

  const query = params.toString();

  return `/sections/${sectionId}/change-requests${query ? `?${query}` : ""}`;
}

export function buildPersonChangeHistoryPath(pgaId: string) {
  return `/people/${pgaId}/person/change-history`;
}
